import gql from 'graphql-tag';
import { ImageFields } from '../common-fields.queries';
import { PriceInfoWithRange, SearchVariantFields } from '../search/search-product-drop-fields.queries';
import { DefaultSearchProductFields } from '../search/search-product-fields.queries';

const ProjectItemFields = gql`
	${ImageFields}
	fragment ProjectItemFields on ProjectGroupItem {
		id
		type
		familyId
		url
		totalPrice
		finish
		productId
		title
		application
		status
		image {
			...ImageFields
			photoId
		}
		manufacturer
		productVariantId
		unitPrice
		wasPrice
		productPricingDisplay
		pricedOptionCount
		quantityPurchased
		quantityUnPurchased
		totalComments
		newComments
		attachedOrders
		stockCount
		specSheet {
			specSheetId
			specSheetName
		}
		options {
			id
			optionName
			optionValue
		}
		hasDuplicate
		originationSource
		baseCategory
	}
`;

export const ProjectPermissionsFields = gql`
	fragment ProjectPermissionsFields on ProjectPermissions {
		commenting
		invite
		showDiscount
		editProject
	}
`;

export const ProjectDetailsGroupFields = gql`
	${ProjectItemFields}
	fragment ProjectDetailsGroupFields on ProjectGroup {
		id
		name
		budget
		cartId
		total
		pending {
			...ProjectItemFields
			subItems {
				...ProjectItemFields
			}
		}
		purchased {
			...ProjectItemFields
			subItems {
				...ProjectItemFields
			}
		}
	}
`;

const ProjectGroupDetailsFields = gql`
	${ProjectPermissionsFields}
	${ProjectDetailsGroupFields}
	fragment ProjectGroupDetailsFields on Project {
		id
		name
		description
		isLocked
		created
		lastModified
		cartId
		permissions {
			...ProjectPermissionsFields
		}
		groups {
			...ProjectDetailsGroupFields
		}
		totals {
			subTotal
			total
			taxTotal
			discount
			specialProductTotal
			discountExpiration {
				expirationDate {
					formatted
				}
				isExpired
				expiresInTenDays
				expiredInLastSixtyDays
			}
		}
	}
`;

export const ProjectCommentFields = gql`
	fragment ProjectCommentFields on ProjectComment {
		id
		author {
			firstName
			lastName
			role
		}
		time
		date
		isNew
		comment
		isUpdated
		canEdit
		isCommentOwner
	}
`;

export const ProjectMemberFields = gql`
	${ProjectPermissionsFields}
	fragment ProjectMemberFields on ProjectMember {
		firstName
		lastName
		email
		id
		customerId
		role
		backgroundColor
		initials
		permissions {
			...ProjectPermissionsFields
		}
	}
`;

export const ProjectExpertFields = gql`
	fragment ProjectExpertFields on ProjectExpert {
		id
		name
		email
		owner
	}
`;

export const ProjectInviteFields = gql`
	fragment ProjectInviteFields on ProjectInvite {
		id
		email
		projectId
		firstName
		lastName
		role
	}
`;

/** Queries */

export const PROJECT_DETAILS_PAGE = gql`
	${ProjectGroupDetailsFields}
	query projectDetailsPage($projectId: Int!) {
		projectById(id: $projectId) {
			... on Project {
				...ProjectGroupDetailsFields
			}
			... on NotFoundError {
				message
			}
		}
	}
`;

export const RECENT_PROJECTS = gql`
	query recentProjects($input: ProjectsSearchCriteriaInput) {
		projects(input: $input) {
			projects {
				id
				name
				lastModified
			}
		}
	}
`;

export const PROJECT_TEAM_MANAGEMENT = gql`
	${ProjectMemberFields}
	${ProjectExpertFields}
	${ProjectInviteFields}
	query projectTeamManagement($projectId: Int!) {
		projectMembers(projectId: $projectId) {
			members {
				...ProjectMemberFields
			}
			experts {
				...ProjectExpertFields
			}
		}
		projectInvites(projectId: $projectId) {
			...ProjectInviteFields
		}
	}
`;

const ProjectProductFinishFields = gql`
	${ImageFields}
	fragment ProjectProductFinishFields on ProjectProductFinish {
		id
		price
		finish
		color
		swatchGradient
		image {
			...ImageFields
		}
		inventory
		productPricingDisplay
		sku
		status
		url
	}
`;

export const GET_PROJECT_FINISHES = gql`
	query ProductFinishes($input: ProjectProductFinishInput!) {
		productFinishes(input: $input) {
			...ProjectProductFinishFields
		}
	}
	${ProjectProductFinishFields}
	${ImageFields}
`;

export const PROJECT_PRODUCT_SEARCH = gql`
	query ProjectProductSearch($input: ProductSearchInput!) {
		productSearch(input: $input) {
			... on ProductSearchResult {
				baseCategory
				businessCategory
				products {
					...DefaultSearchProductFields
					...PriceInfoWithRange
					collection {
						name
						url
					}
					shippingInfo {
						isQuickShip
						message
					}
					attributes {
						name
						values
						id
					}
					variants {
						...SearchVariantFields
					}
				}
				count
				searchId
				correctedSearchTerm
				searchEngine
			}
		}
	}
	${SearchVariantFields}
	${DefaultSearchProductFields}
	${PriceInfoWithRange}
	${ImageFields}
`;

/**
 * Projects specific ProductResource
 */
const ProjectProductResourceFields = gql`
	fragment ProjectProductResourceFields on ProductResource {
		title
		brandName
		url
		variantId
		finishes {
			id
			brandName
			title
			image {
				...ImageFields
			}
			modelNumber
			price
			url
			count
			swatchColor
		}
		price {
			current
		}
	}
	${ImageFields}
`;

/**
 * Projects specific ProductOption
 */
export const ProjectProductOptionFields = gql`
	fragment ProjectProductOptionFields on ProductOption {
		type
		name
		resource {
			...ProjectProductResourceFields
		}
		image {
			...ImageFields
		}
	}
	${ImageFields}
	${ProjectProductResourceFields}
`;

export const GET_REQUIRED_ITEMS_FOR_INSTALLATION = gql`
	query GetRequiredItemsForInstallation($input: GetProductConfigurationInput!) {
		getProductConfiguration(input: $input) {
			required {
				label
				type
				options {
					...ProjectProductOptionFields
				}
				source
			}
		}
	}
	${ProjectProductOptionFields}
`;

export const PROJECT_MEMBERS = gql`
	${ProjectMemberFields}
	${ProjectExpertFields}
	query ProjectMembers($projectId: Int!) {
		projectMembers(projectId: $projectId) {
			members {
				...ProjectMemberFields
			}
			experts {
				...ProjectExpertFields
			}
		}
	}
`;

export const PROJECT_PRODUCT_COMMENTS = gql`
	${ProjectCommentFields}
	query ProjectProductComments($input: ProjectProductCommentsInput!) {
		projectProductComments(input: $input) {
			...ProjectCommentFields
		}
	}
`;

export const PROJECT_INVITES = gql`
	${ProjectInviteFields}
	query ProjectInvites($projectId: Int!) {
		projectInvites(projectId: $projectId) {
			...ProjectInviteFields
		}
	}
`;

export const PROJECT_INVITE = gql`
	${ProjectInviteFields}
	query ProjectInvite($inviteId: String!) {
		projectInvite(inviteId: $inviteId) {
			projectName
			invitedByCustomerName
			...ProjectInviteFields
		}
	}
`;

export const PROJECT_COMMENTS = gql`
	${ProjectCommentFields}
	query ProjectComments($projectId: Int!) {
		projectComments(projectId: $projectId) {
			...ProjectCommentFields
		}
	}
`;

/** Mutations */

export const DELETE_PROJECT_GROUPS = gql`
	${ProjectGroupDetailsFields}
	mutation deleteProjectGroups($input: DeleteProjectGroupInput!) {
		deleteProjectGroups(input: $input) {
			... on Project {
				...ProjectGroupDetailsFields
			}
		}
	}
`;

export const BATCH_UPDATE_PROJECT_GROUPS = gql`
	${ProjectGroupDetailsFields}
	mutation batchMoveProjectItems($input: BatchMoveProjectItemsInput!) {
		batchMoveProjectItems(input: $input) {
			... on Project {
				...ProjectGroupDetailsFields
			}
		}
	}
`;

export const MERGE_PROJECT_GROUP_SOURCES = gql`
	mutation mergeProjectCartsToSessionCart($input: MergeProjectCartsToSessionCartInput!) {
		mergeProjectCartsToSessionCart(input: $input)
	}
`;

export const UPDATE_PROJECT_NAME = gql`
	mutation updateProjectName($projectId: Int!, $name: String!, $description: String) {
		updateProjectName(projectId: $projectId, name: $name, description: $description) {
			... on Project {
				id
				name
				description
			}
		}
	}
`;

export const DELETE_PROJECT_ITEMS = gql`
    mutation deleteProjectItems($input: DeleteProjectItemsInput!) {
        ${ProjectGroupDetailsFields}
        deleteProjectItems(input: $input) {
            ... on Project {
                ...ProjectGroupDetailsFields
            }
        }
    }
`;

export const REORDER_GROUP_ITEMS = gql`
    mutation reorderGroupItems($input: ReorderGroupItemsInput!) {
        ${ProjectGroupDetailsFields}
        reorderGroupItems(input: $input) {
            ... on Project {
                ...ProjectGroupDetailsFields
            }
        }
    }
`;

export const PATCH_PROJECT_GROUP = gql`
    mutation patchProjectGroup($input: PatchGroupInput!) {
        ${ProjectGroupDetailsFields}
        patchProjectGroup(input: $input) {
            ... on Project {
                ...ProjectGroupDetailsFields
            }
            ... on NotFoundError {
                message
            }
        }
    }
`;

export const EDIT_PROJECT_PRODUCT_COMMENT = gql`
	${ProjectCommentFields}
	mutation EditProjectProductComment($input: EditProjectProductCommentInput!) {
		editProjectProductComment(input: $input) {
			... on ProjectComment {
				...ProjectCommentFields
			}
			... on Error {
				message
			}
		}
	}
`;
export const UPDATE_PROJECT_COMMENT = gql`
	${ProjectCommentFields}
	mutation UpdateProjectComment($input: UpdateProjectCommentInput!) {
		updateProjectComment(input: $input) {
			...ProjectCommentFields
		}
	}
`;

export const CREATE_PROJECT_PRODUCT_COMMENT = gql`
	${ProjectCommentFields}
	mutation CreateProjectProductComment($input: CreateProjectProductCommentInput!) {
		createProjectProductComment(input: $input) {
			...ProjectCommentFields
		}
	}
`;

export const CREATE_PROJECT_COMMENT = gql`
	${ProjectCommentFields}
	mutation CreateProjectComment($input: CreateProjectCommentInput!) {
		createProjectComment(input: $input) {
			...ProjectCommentFields
		}
	}
`;

export const DELETE_PROJECT_PRODUCT_COMMENT = gql`
	mutation DeleteProjectProductComment($input: DeleteProjectProductCommentInput!) {
		deleteProjectProductComment(input: $input)
	}
`;
export const DELETE_PROJECT_COMMENT = gql`
	mutation DeleteProjectComment($commentId: Int!, $projectId: Int!) {
		deleteProjectComment(commentId: $commentId, projectId: $projectId)
	}
`;

export const MARK_PROJECT_PRODUCT_COMMENTS_READ = gql`
	mutation MarkProjectProductCommentsRead($input: MarkProjectProductCommentsReadInput!) {
		markProjectProductCommentsRead(input: $input)
	}
`;

export const MARK_PROJECT_COMMENTS_READ = gql`
	mutation MarkProjectCommentsRead($projectId: Int!) {
		markProjectCommentsRead(projectId: $projectId)
	}
`;

export const ASSIGN_PROJECT_EXPERT = gql`
	mutation AssignProjectExpert($projectId: Int!) {
		assignProjectExpert(projectId: $projectId)
	}
`;

export const SEND_PROJECT_INVITE = gql`
	mutation SendProjectInvite($input: SendProjectInviteInput!) {
		sendProjectInvite(input: $input)
	}
`;

export const RESEND_PROJECT_INVITE = gql`
	mutation ResendProjectInvite($inviteId: String!) {
		resendProjectInvite(inviteId: $inviteId)
	}
`;

export const UPDATE_PROJECT_MEMBER_PERMISSIONS = gql`
	${ProjectMemberFields}
	mutation UpdateProjectMemberPermissions($input: UpdateProjectMemberPermissionsInput!) {
		updateProjectMemberPermissions(input: $input) {
			... on ProjectMember {
				...ProjectMemberFields
			}
			... on Error {
				message
			}
		}
	}
`;

export const REMOVE_PROJECT_MEMBER = gql`
	mutation RemoveProjectMember($input: RemoveProjectMemberInput!) {
		removeProjectMember(input: $input)
	}
`;

export const SEND_PROJECT_CARTS = gql`
	mutation SendProjectCarts($input: SendProjectCartsInput!) {
		sendProjectCarts(input: $input)
	}
`;

export const TOGGLE_LOCK_PROJECT_STATUS = gql`
	mutation ToggleProjectLockStatus($projectId: Int!) {
		toggleProjectLockStatus(projectId: $projectId) {
			... on NotFoundError {
				message
			}
			... on Project {
				id
				isLocked
			}
		}
	}
`;

export const ATTACH_ORDER = gql`
	${ProjectGroupDetailsFields}
	mutation AttachOrderToProject($input: AttachOrderToProjectInput!) {
		attachOrderToProject(input: $input) {
			... on NotFoundError {
				message
			}
			... on Project {
				...ProjectGroupDetailsFields
			}
		}
	}
`;

export const PROJECT_ORDERS = gql`
	query ProjectOrders($input: EcrmOrderFilter!) {
		ordersSummary(input: $input) {
			orders {
				orderNumber
				orderDate
				friendlyStatus
				originalTotal
				adjustmentTotal
				adjustedTotal
				creditTotal
				hasAdjustments
				showTracking
				cartItems {
					image
					backordered
				}
			}
		}
	}
`;

export const PROJECT_RETURNS = gql`
	query ProjectReturns($input: EcrmOrderFilter!) {
		returnsSummary(input: $input) {
			returns {
				returnId
				orderNumber
				createdOnDate
				dateCancelled
				mailByDate
				friendlyStatus
				refunds {
					amount
					returnDate
					appliedTo
				}
				cartItems {
					image
				}
			}
		}
	}
`;

export const CREATE_OR_UPDATE_CUSTOM_PRODUCT = gql`
	mutation CreateOrUpdateCustomProduct($input: SpecialProductInput!) {
		createOrUpdateCustomProduct(input: $input) {
			... on SpecialProduct {
				specSheetId
			}
			... on Error {
				message
			}
		}
	}
`;

export const SAVE_CUSTOM_PRODUCT_IMAGE = gql`
	mutation SaveCustomProductImage($input: CustomProductImageInput!) {
		saveCustomProductImage(input: $input) {
			... on CustomImage {
				id
			}
			... on Error {
				message
			}
		}
	}
`;

export const ADD_CUSTOM_PRODUCT_GROUP = gql`
	${ProjectDetailsGroupFields}
	mutation CreateCustomProductGroup($input: ProjectGroupInput!) {
		createGroup(input: $input) {
			... on ProjectGroup {
				...ProjectDetailsGroupFields
			}
		}
	}
`;

export const CHANGE_CART_ITEM_FINISH = gql`
	${ProjectGroupDetailsFields}
	mutation ChangeCartItemFinish($input: ChangeCartItemFinishInput!) {
		changeCartItemFinish(input: $input) {
			... on Project {
				...ProjectGroupDetailsFields
			}
		}
	}
`;

export const DELETE_PROJECT_SUBITEMS = gql`
	${ProjectGroupDetailsFields}
	mutation DeleteProjectSubItems($input: DeleteProjectSubItemInput!) {
		deleteProjectSubItems(input: $input) {
			... on Project {
				...ProjectGroupDetailsFields
			}
		}
	}
`;

export const REMOVE_PROJECT_EXPERT = gql`
	mutation RemoveProjectExpert($projectId: Int!) {
		removeProjectExpert(projectId: $projectId)
	}
`;
